import { AuthService } from "@domain/authentication/auth-service";
import {
	EmailConsentChoices,
	NotificationPreferences,
	PtNotificationPreferencesType,
	User,
	UserUpdateData,
} from "@domain/user/user";
import { UserService } from "@domain/user/user-service";
import { LeavingReason } from "@ui/pages/deletion-flow/leaving-reasons-page";
import { isNotNil } from "@utils/assert";
import axios from "axios";
import { observable } from "micro-observables";
import { Signal } from "micro-signals";

type UserLeavingInfos = { leavingReasons: LeavingReason[] } | null;

export class UserManager {
	private readonly _user = observable<User | null>(null);

	user = this._user.readOnly();

	private _userDeletingInfos: UserLeavingInfos = null;

	onUserLogout = new Signal<void>();
	onUserFirstConnection = new Signal<void>();

	constructor(
		private readonly authService: AuthService,
		private readonly userService: UserService,
	) {}

	async init(): Promise<void> {
		const isAuthenticated = this.authService.isAuthenticated();
		if (isAuthenticated) {
			try {
				await this.retrieveUser();
			} catch (error) {
				console.warn("Authenticated but user does not exist on server");
			}
		}
	}

	async loginWithEmail(email: string, password: string): Promise<void> {
		await this.authService.loginEmail(email, password, this.sendNewDeviceNotification.bind(this));
		await this.retrieveUser();
	}

	async resetPassword(email: string): Promise<void> {
		await this.authService.forgotPassword(email);
	}

	async resendResetToken(email: string): Promise<void> {
		await this.authService.forgotPassword(email);
	}

	async newPassword(email: string, resetToken: string, newPassword: string): Promise<void> {
		await this.authService.newPassword(email, resetToken, newPassword);
	}

	async newPasswordOnBoarding(newPassword: string): Promise<void> {
		await this.authService.completeNewPasswordChallenge(newPassword, this.sendNewDeviceNotification.bind(this));
	}

	async changePassword(currentPassword: string, newPassword: string): Promise<void> {
		await this.authService.changePassword(currentPassword, newPassword);
	}

	async updateUser(data: UserUpdateData): Promise<void> {
		const updatedUser = await this.userService.updateUser(data);
		this._user.set(updatedUser);
	}

	async deleteUser(leavingReasons: LeavingReason[]): Promise<void> {
		this._userDeletingInfos = { leavingReasons };
		await this.userService.deleteUser();
		await this.logout();
	}

	get userDeletingInfos(): UserLeavingInfos {
		const infos = this._userDeletingInfos;
		this._userDeletingInfos = null;
		return infos;
	}

	async logout(): Promise<void> {
		this._user.set(null);
		this.onUserLogout.dispatch();
		await this.authService.logout();
	}

	async setConsentEmails(choices: EmailConsentChoices): Promise<void> {
		const notificationPreferences = (this.user.get()?.notificationPreferences ?? []).map(pref => {
			const isBiogen = pref.type === PtNotificationPreferencesType.BIOGEN;
			const isPhysioMe = pref.type === PtNotificationPreferencesType.PHYSIO_ME;
			let email = pref.email;
			if (isPhysioMe) {
				email = choices.physioMeEmails;
			} else if (isBiogen) {
				email = choices.biogenEmails;
			}
			return {
				...pref,
				email,
			};
		});

		await this.userService.updateNotificationPreferences(notificationPreferences);
	}

	async updateNotificationPreferences(notificationPreferences: NotificationPreferences[]): Promise<void> {
		await this.userService.updateNotificationPreferences(notificationPreferences);
		this._user.update(user => (isNotNil(user) ? { ...user, notificationPreferences } : null));
	}

	async sendNewDeviceNotification(deviceKey: string): Promise<void> {
		console.debug("[UserManager] sendNewDeviceNotification");
		try {
			await this.userService.sendNewDeviceNotification(deviceKey);
		} catch (error) {
			console.warn("[UserManager] could not send new device notification", error);
		}
	}

	async retrieveUser(): Promise<void> {
		try {
			const user = await this.userService.getUser();
			this._user.set(user);
			if (!user.lastConnectionDate) {
				this.onUserFirstConnection.dispatch();
			}
		} catch (error) {
			// Network errors (no internet) should not trigger logout — only server/auth errors should
			const isNetworkError = axios.isAxiosError(error) && !error.response;
			if (!isNetworkError) {
				await this.logout();
			}
			throw error;
		}
	}

	async retrieveUserEmail(userId: string): Promise<string | null> {
		return await this.userService.getEmailFromUserId(userId);
	}

	updateUserHasIdentityFiles(hasIdentityFiles: boolean): void {
		const user = this.user.get();
		if (isNotNil(user)) {
			this._user.set({ ...user, hasIdentityFiles });
		}
	}

	async foo(): Promise<boolean> {
		console.debug("[UserManager] foo method called");
		return true;
	}
}
